import styled from "@emotion/styled";
import React, { FC, forwardRef, Fragment, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { ReactSortable } from "react-sortablejs";

import {
  TemplateCardFavouriteVariant,
  TemplateCardFavouriteViewProps,
} from "../../../cards/TemplateCard";

import { useIsDesktop } from "../../../../../hooks/viewport/useViewportCategory";
import { withLoaderHOC, WithLoaderHOC } from "../../../../atoms/Loader";
import TemplateCardKpiVariant, {
  TemplateCardKpiViewProps,
} from "../../../cards/TemplateCard/variants/kpi";

const Container = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: 0.7rem;
  justify-content: flex-start;
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const GridCustomComponent = forwardRef<HTMLDivElement, any>((props, ref) => (
  <Container ref={ref}>{props.children}</Container>
));

const FavouriteVariant: FC<Props> = ({
  templateData,
  changeOrderOfFavourite,
  toggleModal,
}) => {
  const { formatMessage } = useIntl();
  const isDesktop = useIsDesktop();

  const [sortedData, setSortedData] = useState<TemplateData[]>([]);

  useEffect(() => {
    setSortedData(templateData);
  }, [setSortedData, templateData]);

  const renderCard = (item: TemplateData) => {
    const { isKpi } = item;

    if (isKpi) {
      return (
        <TemplateCardKpiVariant
          {...item}
          key={item.templateId}
          onClick={toggleModal}
        />
      );
    }

    return (
      <TemplateCardFavouriteVariant
        {...item}
        key={item.templateId}
        onClick={toggleModal}
      />
    );
  };

  if (sortedData.length === 0) {
    const noItemsMsg = formatMessage({ id: "common.empty_list" });
    return <Fragment>{noItemsMsg}</Fragment>;
  }

  if (!isDesktop) {
    return (
      <GridCustomComponent>{sortedData.map(renderCard)}</GridCustomComponent>
    );
  }

  return (
    <ReactSortable
      list={sortedData}
      setList={setSortedData}
      onEnd={(evt, sortable) => {
        const beforeEl = templateData[(evt.newIndex as number) - 1];
        const movedEl = templateData[evt.oldIndex as number];

        if (beforeEl) {
          changeOrderOfFavourite(movedEl.favouriteId, beforeEl.order + 1);
        } else {
          changeOrderOfFavourite(movedEl.favouriteId, 0);
        }
      }}
      tag={GridCustomComponent}
    >
      {sortedData.map(renderCard)}
    </ReactSortable>
  );
};

export type TemplateData = TemplateCardFavouriteViewProps &
  TemplateCardKpiViewProps & {
    isKpi: boolean;
    id: number;
    favouriteId: number;
    order: number;
  };

type Props = {
  templateData: TemplateData[];
  toggleModal?: () => void;
  changeOrderOfFavourite: (id: number, order: number) => void;
};

export default withLoaderHOC(FavouriteVariant);
export type ControlledProps = WithLoaderHOC<Props>;
